
import { Component, OnDestroy, OnInit } from "@angular/core";
import { VTableRowStatus } from "@app/directives/_layout/v-table/_enums/VTableRowStatus.enum";
import { IVTableCell } from "@app/directives/_layout/v-table/_models/IVTableCell";
import { IVTableColumn } from "@app/directives/_layout/v-table/_models/IVTableColumn";
import { IVTableRow } from "@app/directives/_layout/v-table/_models/IVTableRow";
import { FallbackCellType, VTableService } from "@app/directives/_layout/v-table/_services/v-table.service";
import { DepotNameComponent } from "@app/directives/_misc/depotName/depotName.component";
import { IAggregateFilterEvent, IFilterBarTab } from "@app/directives/_resourceFilters/filter-bar/filter-bar.models";
import { DateRange } from "@app/enum/DateRange.enum";
import { SortDirection } from "@app/enum/SortDirection";
import { SortType } from "@app/enum/SortType";
import { IPlot } from "@app/interfaces/IPlot";
import { IGroupWithIds } from "@app/main/components/resourceManagement/groups/_models/IGroupWithIds";
import { IDateQueryOptions } from "@app/services/accelerometerRecord.service";
import { DateService } from "@app/services/date.service";
import { DriverService } from "@app/services/driver.service";
import { DriverAssignmentService } from "@app/services/driverAssignment.service";
import { GroupService } from "@app/services/groups/group.service";
import { TranslationService } from "@app/services/translation.service";
import { VehicleService } from "@app/services/vehicle.service";
import { Subscription, timer } from "rxjs";
import { AlertReviewStatus } from "../../_enums/AlertReviewStatus.enums";
import { AlertCategory } from "../../_enums/alerts.enums";
import { IAlertRule } from "../../_models/alerts.interfaces";
import { IAlertReviewHistory } from "../../_models/IAlertReviewHistory";
import { AlertReviewService } from "../../_services/alert-review.service";
import { AlertRecordMaterialisationOptions, AlertsService, IAlertRecordQueryOptions } from "../../_services/alerts.service";
import { AlertCategoryComponent } from "../alert-category/alert-category.component";
import { AlertDetailService } from "../alert-detail/alert-detail.service";
import { AxleWeightAlertDetailComponent } from "../alert-detail/axle-weight-alert-detail/axle-weight-alert-detail.component";
import { GeofenceAlertDetailComponent } from "../alert-detail/geofence-alert-detail/geofence-alert-detail.component";
import { InputAlertDetailComponent } from "../alert-detail/input-alert-detail/input-alert-detail.component";
import { PowerLossAlertDetailComponent } from "../alert-detail/power-loss-alert-detail/power-loss-alert-detail.component";
import { TemperatureAlertDetailComponent } from "../alert-detail/temperature-alert-detail/temperature-alert-detail.component";
import { TollAlertDetailComponent } from "../alert-detail/toll-alert-detail/toll-alert-detail.component";
import { TyreRecordAlertDetailComponent } from "../alert-detail/tyre-record-alert-detail/tyre-record-alert-detail.component";
import { VehicleDiagnosticAlertDetailComponent } from "../alert-detail/vehicle-diagnostic-detail/vehicle-diagnostic-alert-detail.component";
import { VideoAlertDetailComponent } from "../alert-detail/video-alert-detail/video-alert-detail.component";
import "./alert-review-history.component.scss";

@Component({
    selector: 'alert-review-history',
    template: require('./alert-review-history.component.html')
})
export class AlertReviewHistoryComponent implements OnInit, OnDestroy {
    loading = false;

    //// #Tables
    tableColumns: IVTableColumn<IAlertReviewHistory>[];
    tableRows: IVTableRow[];

    alertReviewHistory: IAlertReviewHistory[];
    alertRules: IAlertRule[];
    depots: IGroupWithIds[];

    defaultDateRange: DateRange = DateRange.Latest7Days;
    startDate: Date;
    endDate: Date;
    groupFilter: IGroupWithIds;

    filterTabs: IFilterBarTab<IAlertReviewHistory>[] = [];

    tabIndex: number = 1;

    updateInterval: ReturnType<typeof setInterval>;

    subscriptions = new Subscription();

    constructor(
        private readonly alertDetailService: AlertDetailService,
        private readonly alertsService: AlertsService,
        private readonly alertReviewService: AlertReviewService,
        private readonly driverService: DriverService,
        private readonly driverAssignmentService: DriverAssignmentService,
        private readonly groupService: GroupService,
        private readonly translationService: TranslationService,
        private readonly vehicleService: VehicleService,
        private readonly dateService: DateService,
        private readonly vTableService: VTableService,
    ) {
    }

    ngOnInit(): void {
        this.createTabs();

        this.startDate = this.dateService.dateRangeFromEnum(this.defaultDateRange).start;
        this.endDate = this.dateService.dateRangeFromEnum(this.defaultDateRange).end;

        this.subscriptions.add(timer(0, 60_000).subscribe((iteration: number) => {
            this.loadData(iteration === 0, true);
        }));

        this.subscriptions.add(this.alertReviewService.alertReviewHistoryUpdated$.subscribe((history) => {
            this.alertReviewService.updateAlertReviewHistoryItem(this.alertReviewHistory, history);

            this.createTableRows();
        }));
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    selectedItemChanged(row: IVTableRow) {
        this.alertReviewService.openIncidentDetailsDialog(row.data, 'app.alerts.review');
    }

    onFilterChanged(filterEvent: IAggregateFilterEvent<IAlertReviewHistory>): void {
        if (!filterEvent.trigger) {
            return;
        }

        if (filterEvent.trigger === 'date') {
            this.startDate = filterEvent.date[0];
            this.endDate = filterEvent.date[1];

            this.loadData(true, true);
        }
    }

    // ---

    private loadData(firstLoad = false, invalidateCache = false): void {

        if (firstLoad) {
            this.loading = true;
        }

        let promises: any[] = [];

        promises.push(this.vehicleService.getVehicles());
        promises.push(this.driverService.getDrivers());
        promises.push(this.driverAssignmentService.getDriverAssignments(true));
        promises.push(this.groupService.getGroupsWithIds());
        promises.push(this.alertsService.getAlertRules());

        const dateQueryOptions: IDateQueryOptions = {
            Start: this.startDate,
            End: this.endDate,
            invalidateCache: invalidateCache
        }

        promises.push(this.alertReviewService.getAlertReviewHistory(dateQueryOptions));
        promises.push(this.alertReviewService.getAlertReviewEvents({ ...dateQueryOptions, End: new Date() }));

        const alertRecordQueryOptions = dateQueryOptions as IAlertRecordQueryOptions;
        alertRecordQueryOptions.MaterialisationOptions = AlertRecordMaterialisationOptions.WithMetadata;

        promises.push(this.alertsService.getAlertRuleRecordsUsingQueryOptions(alertRecordQueryOptions));

        Promise.all(promises)
            .then(([vehicles, drivers, assignments, groups, alertRules, alertReviewHistory, alertReviewEvents, alertRecords]) => {

                this.alertReviewHistory = alertReviewHistory;

                this.depots = groups.filter(group => group.IsDepot);

                this.driverAssignmentService.mapDriversToVehicles(drivers, assignments, vehicles);

                this.alertRules = alertRules;

                this.alertReviewHistory.forEach(history => {
                    this.alertReviewService.mapVehicleToHistory(history, vehicles);
                    this.alertReviewService.mapEventsToHistory(history, alertReviewEvents);
                    this.alertReviewService.mapAlertRecordToHistory(history, alertRecords);

                    if (history.AlertRecord) {
                        this.alertsService.parseAlertRecordMetadata(history.AlertRecord);
                    }

                });

                if (this.groupFilter) {

                    const vehicleIds = this.groupFilter.VehicleIds;
                    this.alertReviewHistory = this.alertReviewHistory.filter(history => {
                        if (vehicleIds.includes(history.VehicleId)) {
                            return history;
                        }
                    });
                }

                this.createTable();

            }).finally(() => {
                this.createTableRows();
                this.loading = false;
            });
    }

    private createTableRows(): void {
        this.tableRows = this.vTableService.createTableRows(this.alertReviewHistory, this.tableColumns);
    }

    private createTable(): void {
        this.tableColumns = [
            this.vTableService.createTableColumn(
                this.vTableService.createStatusTableHeader({ sorted: false, minTableWidth: 490 }),
                (historyItem) => {
                    const rowStatus = this.setRowWarningStatus(historyItem);

                    return this.vTableService.createStatusCell(rowStatus);
                }
            ),
            this.vTableService.createTableColumn(
                this.vTableService.createTableHeader({ title: "ID", sortable: true, width: 100 }),
                (historyItem) => this.vTableService.createTableCell(historyItem.Id)
            ),
            this.vTableService.createTableColumn(
                this.vTableService.createDateTableHeader({ tKey: "Alerts.DateRaised", sorted: true, sortDirection: SortDirection.Descending, minTableWidth: 800 }),
                (historyItem) => this.vTableService.createDateCell(new Date(historyItem.StartTime))
            ),
            this.vTableService.createTableColumn(
                this.vTableService.createTableHeader({ title: this.translationService.t('Alerts.Status'), sortable: true, width: 100, minTableWidth: 390 }),
                (historyItem) => {
                    const status: string = this.translationService.tEnum(AlertReviewStatus, 'AlertReviewStatus', this.alertReviewService.getCurrentStatusOfHistoryItem(historyItem));

                    return this.vTableService.createTableCell(status);
                },
            ),
            this.vTableService.createTableColumn(
                this.vTableService.createTableHeader({ title: this.translationService.t('Alerts.OpenDuration'), sortable: true, sortType: SortType.Number, width: 200, minTableWidth: 1040 }),
                (historyItem) => {
                    const { startTime, endTime } = this.alertReviewService.getStartAndEndDates(historyItem);

                    return this.vTableService.createDurationCellFromDates(startTime, endTime);
                },
            ),
            this.vTableService.createTableColumn(
                this.vTableService.createTableHeader({ title: this.translationService.t('Common.Vehicle'), sortable: true, width: 100 }),
                (historyItem) => this.vTableService.createVehicleRegCell(historyItem.Vehicle),
            ),
            this.vTableService.createTableColumn(
                this.vTableService.createTableHeader({ title: this.translationService.t('Common.Driver'), sortable: true, width: 150, minTableWidth: 1170 }),
                (historyItem) => this.vTableService.createTableCell(historyItem.Vehicle?.Driver?.DisplayName, {}, FallbackCellType.NA),
            ),
            this.vTableService.createTableColumn(
                this.vTableService.createTableHeader({ title: this.translationService.t('Alerts.Depot'), sortable: true, width: 150, minTableWidth: 1295 }),
                (historyItem) => {
                    const depot = this.depots.find(x => x.VehicleIds.includes(historyItem.VehicleId));
                    return this.vTableService.createTableCell(depot?.Name, { component: DepotNameComponent, componentInputs: { name: depot?.Name } }, FallbackCellType.NA);
                },
            ),
            this.vTableService.createTableColumn(
                this.vTableService.createTableHeader({ title: this.translationService.t('Alerts.Category'), sortable: true, width: 150 }),
                (historyItem) => {
                    const category: string = this.translationService.tEnum(AlertCategory, 'AlertCategory', this.alertReviewService.getAlertRuleCategory(historyItem, this.alertRules));
                    const { subCategoryEnum, subCategoryString } = this.alertsService.getAlertRuleSubcategoryEnum(this.alertReviewService.getAlertRuleCategory(historyItem, this.alertRules));
                    const subCategory: string = this.translationService.tEnum(subCategoryEnum, subCategoryString, this.alertReviewService.getAlertRuleSubCategory(historyItem, this.alertRules));

                    return this.vTableService.createTableCell(category, { exportData: `${category} ${subCategory ? ' ' + subCategory : ''}`, component: AlertCategoryComponent, componentInputs: { category, subCategory } }, FallbackCellType.NA);
                },
            ),
            this.vTableService.createTableColumn(
                this.vTableService.createTableHeader({ title: this.translationService.t('Alerts.Information'), width: 150, searchable: false, sortable: false, minTableWidth: 666 }),
                (historyItem) => this.createInformationCell(historyItem),
            ),
            this.vTableService.createTableColumn(
                this.vTableService.createTableHeader({ title: this.translationService.t('Common.Location'), minTableWidth: 1450 }),
                (historyItem) => {
                    const plot: IPlot = historyItem.AlertRecord?.plot ? historyItem.AlertRecord.plot : {};

                    return this.vTableService.createLocationInformationCell({ plot });
                }
            ),
        ]
    }

    private createInformationCell(historyItem: IAlertReviewHistory): IVTableCell {
        if (!historyItem.AlertRecord) {
            return this.vTableService.createTableCell(this.translationService.t('Alerts.NoAlarmData'))
        }

        switch (true) {
            case !!historyItem.AlertRecord.alertMetadata?.AxleWeightOutOfBoundsHistoryId:
            case !!historyItem.AlertRecord.alertMetadata?.AxleWeightProfileId:
                return this.vTableService.createTableCell(historyItem, { component: AxleWeightAlertDetailComponent, componentInputs: { record: historyItem.AlertRecord, tableMode: true }, exportDataMethod: () => this.detailExportMethod(historyItem) });

            case !!historyItem.AlertRecord.alertMetadata?.GeofenceId:
                return this.vTableService.createTableCell(historyItem, { component: GeofenceAlertDetailComponent, componentInputs: { record: historyItem.AlertRecord, tableMode: true }, exportDataMethod: () => this.detailExportMethod(historyItem) });

            case !!historyItem.AlertRecord.alertMetadata?.InputId:
                return this.vTableService.createTableCell(historyItem, { component: InputAlertDetailComponent, componentInputs: { record: historyItem.AlertRecord, tableMode: true }, exportDataMethod: () => this.detailExportMethod(historyItem) });

            case !!historyItem.AlertRecord.alertMetadata?.PowerLossHistoryId:
                return this.vTableService.createTableCell(historyItem, { component: PowerLossAlertDetailComponent, componentInputs: { record: historyItem.AlertRecord, tableMode: true }, exportDataMethod: () => this.detailExportMethod(historyItem) });

            case !!historyItem.AlertRecord.alertMetadata?.TemperatureRecordId:
            case !!historyItem.AlertRecord.alertMetadata?.TemperatureAlarmRecordId:
                return this.vTableService.createTableCell(historyItem, { component: TemperatureAlertDetailComponent, componentInputs: { record: historyItem.AlertRecord, tableMode: true }, exportDataMethod: () => this.detailExportMethod(historyItem) });

            case !!historyItem.AlertRecord.alertMetadata?.TollId:
                return this.vTableService.createTableCell(historyItem, { component: TollAlertDetailComponent, componentInputs: { record: historyItem.AlertRecord, tableMode: true }, exportDataMethod: () => this.detailExportMethod(historyItem) });

            case !!historyItem.AlertRecord.alertMetadata?.TyreRecordDataId:
                return this.vTableService.createTableCell(historyItem, { component: TyreRecordAlertDetailComponent, componentInputs: { record: historyItem.AlertRecord, tableMode: true }, exportDataMethod: () => this.detailExportMethod(historyItem) });

            case !!historyItem.AlertRecord.alertMetadata?.VehicleDiagnosticRecordId:
                return this.vTableService.createTableCell(historyItem, { component: VehicleDiagnosticAlertDetailComponent, componentInputs: { record: historyItem.AlertRecord, tableMode: true }, exportDataMethod: () => this.detailExportMethod(historyItem) });

            case !!historyItem.AlertRecord.alertMetadata?.VideoEntryId:
                return this.vTableService.createTableCell(historyItem, { component: VideoAlertDetailComponent, componentInputs: { record: historyItem.AlertRecord, tableMode: true }, exportDataMethod: () => this.detailExportMethod(historyItem) });            

            default:
                return this.vTableService.createTableCell(this.translationService.t('Alerts.NoAlarmData'))
        }
}

    private detailExportMethod(historyItem): Promise<string> {
        return this.alertDetailService.getAlertDetailExport(historyItem.AlertRecord);
    }

    private setRowWarningStatus(historyItem: IAlertReviewHistory): VTableRowStatus {
        let status: VTableRowStatus = VTableRowStatus.Info;
        if (this.alertReviewService.getCurrentStatusOfHistoryItem(historyItem) === AlertReviewStatus.Closed) {
            status = VTableRowStatus.Success;
        }
        return status;
    }

    private createTabs(): void {
        this.filterTabs = [];

        this.createTabDefinition("Common.All", (r) => true);
        this.createTabDefinition("Alerts.Open", (r) => r.currentStatus === AlertReviewStatus.Open);
        this.createTabDefinition("Alerts.Closed", (r) => r.currentStatus === AlertReviewStatus.Closed);
    }

    private createTabDefinition(translationString: string, callback: (historyItem: IAlertReviewHistory) => boolean) {

        this.filterTabs.push({ title: this.translationService.t(translationString), predicate: callback });
    }

}


